export const ERROR_CODES = [
  'not_found',
  'validation_failed',
  'unauthorized',
  'forbidden',
  'quota_exceeded',
  'conflict',
  'already_exists',
  'upstream_failed',
  'internal_error',
  'payload_too_large',
  'rate_limited',
  'timeout',
] as const

export type ErrorCode = (typeof ERROR_CODES)[number]

export const OFFLINE = 'offline' as const

export interface ApiFailure {
  code: string
  message: string
  details?: Record<string, string>
  request_id?: string
}

export class ApiError extends Error {
  readonly code: ErrorCode | typeof OFFLINE | 'unknown'
  readonly status: number
  readonly details: Record<string, string>
  readonly requestId: string | undefined

  // body is what the server actually sent, when it sent something other than
  // the error envelope. A refused survey answer comes back as 409 with the
  // question the person is really on, so that the screen can redraw without
  // asking again (§3.11 of docs/plan/survey.md).
  readonly body: unknown

  constructor(status: number, failure: ApiFailure, body?: unknown) {
    super(failure.message || failure.code)
    this.name = 'ApiError'
    this.code = codeOf(failure.code)
    this.status = status
    this.details = failure.details ?? {}
    this.requestId = failure.request_id
    this.body = body
  }
}

function codeOf(raw: string): ErrorCode | typeof OFFLINE | 'unknown' {
  if (raw === OFFLINE) {
    return OFFLINE
  }

  return (ERROR_CODES as readonly string[]).includes(raw) ? (raw as ErrorCode) : 'unknown'
}

// failureOf is the last line of defence: the response middleware already turns
// every failed request into an ApiError, so anything arriving here is a shape
// nobody predicted rather than an API error, and it still has to be an Error.
export function failureOf(cause: unknown): Error {
  return cause instanceof Error
    ? cause
    : new ApiError(0, { code: 'unknown', message: String(cause) })
}

export function offlineError(cause: unknown): ApiError {
  return new ApiError(0, {
    code: OFFLINE,
    message: cause instanceof Error ? cause.message : String(cause),
  })
}

export function isApiError(error: unknown): error is ApiError {
  return error instanceof ApiError
}

export function needsReauthorization(error: unknown): boolean {
  return isApiError(error) && error.code === 'unauthorized'
}

export function needsUpgrade(error: unknown): boolean {
  return isApiError(error) && error.code === 'quota_exceeded'
}

export function isOffline(error: unknown): boolean {
  return isApiError(error) && error.code === OFFLINE
}

export function isRetryable(error: unknown): boolean {
  if (!isApiError(error)) {
    return false
  }

  return error.code === OFFLINE || error.status === 502 || error.status === 503
}
